import React, { useState } from "react";
import { auth, db } from "../firebase";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AdminProducts from "./sections/AdminProducts";
import AdminOrders from "./sections/AdminOrders";
import AdminHomepage from "./sections/AdminHomepage";
import AdminAbout from "./sections/AdminAbout";
import AdminContact from "./sections/AdminContact";
import AdminBlog from "./sections/AdminBlog";
import AdminPayments from "./sections/AdminPayments";
import AdminShipping from "./sections/AdminShipping";
import AdminDiscounts from "./sections/AdminDiscounts";
import AdminReports from "./sections/AdminReports";
import AdminSettings from "./sections/AdminSettings";
import AdminSEO from "./sections/AdminSEO";
import AdminBookstores from "./sections/AdminBookstores";
import AdminNavbar from "./sections/AdminNavbar";
import AdminCanvas from "./sections/AdminCanvas";
import "./AdminDashboard.css";
import {
    FiPackage, FiShoppingBag, FiLayout, FiInfo,
    FiMail, FiLogOut, FiMenu, FiX, FiFileText,
    FiCreditCard, FiTruck, FiTag, FiPieChart, FiSettings, FiSearch, FiMapPin,
    FiBookOpen
} from "react-icons/fi";

const SECTIONS = [
    { id: "products", label: "Products",       icon: FiPackage },
    { id: "orders",   label: "Orders",         icon: FiShoppingBag },
    { id: "blog",     label: "News / Blog",    icon: FiFileText },
    { id: "canvas",   label: "Lore Canvas",    icon: FiBookOpen },
    { id: "homepage", label: "Home Sections",  icon: FiLayout },
    { id: "about",    label: "About Page",     icon: FiInfo },
    { id: "contact",  label: "Contact Info",   icon: FiMail },
    { id: "payments", label: "Payments",       icon: FiCreditCard },
    { id: "shipping", label: "Shipping",       icon: FiTruck },
    { id: "discounts", label: "Discounts",      icon: FiTag },
    { id: "reports",   label: "Reports",        icon: FiPieChart },
    { id: "settings",  label: "Settings",       icon: FiSettings },
    { id: "seo",       label: "SEO / Meta",     icon: FiSearch },
    { id: "navbar",    label: "Navigation",     icon: FiMenu },
    { id: "bookstores", label: "Local Bookstores", icon: FiMapPin },
];

export default function AdminDashboard() {
    const { user } = useAuth();
    const [active, setActive] = useState("products");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [newCount, setNewCount] = useState(0);

    const [config, setConfig] = useState(null);

    React.useEffect(() => {
        // Listen for order count
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const q = query(collection(db, "orders"), where("createdAt", ">", oneDayAgo));
        const unsubOrders = onSnapshot(q, (snap) => {
            setNewCount(snap.size);
        });

        // Listen for general config (logo, name)
        const unsubConfig = onSnapshot(doc(db, "siteConfig", "general"), (snap) => {
            if (snap.exists()) setConfig(snap.data());
        });

        return () => {
            unsubOrders();
            unsubConfig();
        };
    }, []);

    const ActiveSection = {
        products: AdminProducts,
        orders:   AdminOrders,
        blog:     AdminBlog,
        canvas:   AdminCanvas,
        homepage: AdminHomepage,
        about:    AdminAbout,
        contact:  AdminContact,
        payments: AdminPayments,
        shipping: AdminShipping,
        discounts: AdminDiscounts,
        reports: AdminReports,
        settings: AdminSettings,
        seo: AdminSEO,
        navbar: AdminNavbar,
        bookstores: AdminBookstores,
    }[active];

    return (
        <div className="admin-shell">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div className="admin-overlay" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Sidebar */}
            <aside className={`admin-sidebar ${sidebarOpen ? "open" : ""}`}>
                <div className="admin-sidebar-header-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 15 }}>
                    <Link to="/" className="admin-sidebar-header" style={{ textDecoration: "none", color: "inherit", borderBottom: "none", margin: 0, flex: 1 }}>
                        <img
                            src={config?.siteLogo || "https://pe56d.s3.amazonaws.com/o_1j7cpvf4dh1f1h3djaa1jja2hln.png"}
                            alt={config?.siteName || "LB Books"}
                            className="admin-sidebar-logo"
                        />
                        <span>Admin</span>
                    </Link>
                    <button 
                        className="admin-sidebar-close-mobile"
                        onClick={() => setSidebarOpen(false)}
                        style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", display: "none" }}
                    >
                        <FiX size={24} />
                    </button>
                </div>

                <nav className="admin-nav">
                    {SECTIONS.map(s => (
                        <button
                            key={s.id}
                            className={`admin-nav-item ${active === s.id ? "active" : ""}`}
                            onClick={() => { setActive(s.id); setSidebarOpen(false); }}
                            style={{ position: "relative" }}
                        >
                            <s.icon size={18} />
                            <span>{s.label}</span>
                            {s.id === "orders" && newCount > 0 && (
                                <span className="admin-nav-badge">{newCount}</span>
                            )}
                        </button>
                    ))}
                </nav>

                <div className="admin-sidebar-footer">
                    <p className="admin-user-email">{user?.email}</p>
                    <button
                        className="admin-logout-btn"
                        onClick={() => signOut(auth)}
                    >
                        <FiLogOut size={16} />
                        Sign Out
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <div className="admin-main">
                <header className="admin-topbar">
                    <button
                        className="admin-menu-toggle"
                        onClick={() => setSidebarOpen(o => !o)}
                    >
                        {sidebarOpen ? <FiX size={22} /> : <FiMenu size={22} />}
                    </button>
                    <h2 className="admin-page-title">
                        {SECTIONS.find(s => s.id === active)?.label}
                    </h2>
                    <Link to="/" className="admin-view-site">
                        <FiLayout size={16} />
                        <span>View Site</span>
                    </Link>
                </header>

                <div className="admin-content">
                    <ActiveSection />
                </div>
            </div>
        </div>
    );
}
